<template>
    <div
        v-if="availableFilters"
        class="card my-4 p-4">
        <div
            class="row"
            :class="{'mb-2': showTextFilter}">
            <div
                v-if="showTextFilter"
                class="col-12">
                <label class="form-control-label">
                    {{ $t('iFrame.filterSettings.freeText') }}
                </label>
            </div>
            <div class="col-12 d-flex gap-4 align-items-center">
                <div class="flex-grow-1">
                    <base-input
                        v-if="showTextFilter"
                        v-model="filterText"
                        no-margin
                        :name="iFrameType === 'appointments' ? $t('iFrame.filterSettings.freeTextPlaceholderAppointments') : $t('iFrame.filterSettings.freeTextPlaceholder')"
                        :placeholder="iFrameType === 'appointments' ? $t('iFrame.filterSettings.freeTextPlaceholderAppointments') : $t('iFrame.filterSettings.freeTextPlaceholder')" />
                    <h2 v-else>
                        <span v-if="iFrameType === 'events'">{{ $t('iFrame.filterSettings.titleEvents') }}</span>
                        <span v-if="iFrameType === 'appointments'">{{
                            $t('iFrame.filterSettings.titleAppointments')
                        }}</span>
                    </h2>
                </div>
                <div v-if="showMoreFilterArea">
                    <base-button
                        type="primary"
                        outline
                        class="collapser"
                        :class="{'is-collapsed': !open}"
                        @click="open = !open">
                        <font-awesome-icon
                            class="chevron"
                            icon="chevron-up"
                            fixed-width
                            size="2x" />
                    </base-button>
                </div>
            </div>
        </div>
        <transition name="collapsible">
            <div v-show="open && showMoreFilterArea">
                <div class="row">
                    <div
                        v-if="showRangeFilter || showEventTypeFilter"
                        class="col-md-6">
                        <div
                            v-if="showRangeFilter"
                            class="row">
                            <div class="col-12 col-sm-6">
                                <base-input
                                    v-model="dateFrom"
                                    input-classes="pointer"
                                    :rules="{dateInValidRange: true}"
                                    :label="$t('iFrame.filterSettings.dateRangeFrom')"
                                    type="date" />
                            </div>
                            <div class="col-12 col-sm-6">
                                <base-input
                                    v-model="dateTo"
                                    input-classes="pointer"
                                    :rules="{dateInValidRange: true}"
                                    :label="$t('iFrame.filterSettings.dateRangeTo')"
                                    type="date" />
                            </div>
                        </div>
                        <div
                            v-if="showEventTypeFilter"
                            class="row">
                            <div class="col-12">
                                <base-input :label="$t('iFrame.filterSettings.eventType')">
                                    <div class="d-flex flex-row gap-3">
                                        <BaseCheckbox
                                            v-for="type in availableTypes"
                                            :key="'type-options-' + type.id"
                                            :checked="selectedType && selectedType.includes(type.id)"
                                            :label="type.Bezeichnung"
                                            @input="checkType($event, type.id)">
                                            {{ type.Bezeichnung }}
                                        </BaseCheckbox>
                                    </div>
                                </base-input>
                            </div>
                        </div>
                    </div>
                    <div
                        class="col-12"
                        :class="{'col-md-6': showEventTypeFilter || showRangeFilter}">
                        <template v-if="showCategoryFilter">
                            <label class="form-control-label">{{ $t('iFrame.filterSettings.categories') }}</label>
                            <EventCategoriesTree
                                v-if="showCategoriesAsTree"
                                ref="tree"
                                v-model:checkedEventCategories="selectedCategories"
                                :tree-data="requestedCategoryTree" />
                            <div
                                v-else
                                class="d-flex gap-2 my-1 flex-wrap">
                                <SelectableCategory
                                    v-for="category in shownCategories"
                                    :key="'category-option-' + category.id"
                                    v-model="selectedCategories"
                                    :value="category.id"
                                    :label="category.Bezeichnung" />
                                <SelectableCategory
                                    v-if="availableCategories.length > maxCategoriesCollapsed && !showAllCategories"
                                    v-model="showAllCategories"
                                    :label="$t('iFrame.filterSettings.showAllCategories')" />
                            </div>
                            <div
                                v-if="selectedCategories && selectedCategories.length > 1"
                                class="mt-2">
                                <BaseCheckbox
                                    v-model="forceAllCategories"
                                    :name="$t('iFrame.filterSettings.forceAllCategories')">
                                    <small v-if="iFrameType === 'events'">{{
                                        $t('iFrame.filterSettings.forceAllEventCategories')
                                    }}</small>
                                    <small v-if="iFrameType === 'appointments'">{{
                                        $t('iFrame.filterSettings.forceAllAppointmentCategories')
                                    }}</small>
                                </BaseCheckbox>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import SelectableCategory from "@/components/pages/eventIFrame/FilterSettings/SelectableCategory";
import filterSettingsMixin from "@/mixins/eventIFrame/filterSettingsMixin";

export default {
    name: "IFrameFilterSettings",
    components: {SelectableCategory},
    mixins: [filterSettingsMixin],
    data() {
        return {
            maxCategoriesCollapsed: 10,
            showAllCategories: false,
            open: false
        }
    },
    computed: {
        availableCategories() {
            return [...this.availableFilters.categoriesUsedInEvents]
        },
        availableTypes() {
            return [...this.availableFilters.eventTypesUsedInEvents]
        },
        requestedCategoryTree() {
            if (!this.availableFilters?.mainCategoriesRequestedBySettings) return []
            return this.mapNodesWithChildren(this.availableFilters.mainCategoriesRequestedBySettings)
        },
        showCategoriesAsTree() {
            return !!this.requestedCategoryTree?.length
        },
        shownCategories() {
            if (this.availableCategories.length > this.maxCategoriesCollapsed && !this.showAllCategories) {
                return this.availableCategories.slice(0, this.maxCategoriesCollapsed)
            }
            return this.availableCategories
        }
    },
    methods: {
        mapNodesWithChildren(nodes) {
            if (!nodes) return []
            return nodes.map((node) => {
                return {
                    id: node.id,
                    Bezeichnung: node.Bezeichnung,
                    disabled: !this.availableCategories || !this.availableCategories.some(category => category.id === node.id),
                    childVeranstaltungskategorien: this.mapNodesWithChildren(node.childVeranstaltungskategorien)
                }
            })
        },
        checkType(check, id) {
            let newTypes = this.selectedType.filter(selected => selected !== id)
            if (check) {
                newTypes = [...newTypes, id]
            }
            this.selectedType = newTypes
        }
    }
}
</script>

<style lang="scss" scoped>
.collapser {
    .chevron {
        transition: transform 0.3s;
    }

    &.is-collapsed {
        .chevron {
            transform: rotate(180deg);
        }
    }
}

.collapsible-enter-active, .collapsible-leave-active {
    transition: max-height 0.5s;
    overflow: hidden;
}

.collapsible-enter, .collapsible-leave-to {
    max-height: 0;
}

.collapsible-enter-to, .collapsible-leave {
    max-height: 700px;
}
</style>
